import {chain, get} from 'lodash';
import moment from 'moment';
import React, {useMemo} from 'react';
import {Redirect, useParams} from 'react-router-dom/cjs/react-router-dom.min';
import {useChatSession} from '../../../../data/hooks/chat/useChatSession';
import {useChatSessions} from '../../../../data/hooks/chat/useChatSessions';
import {useEvent} from '../../../../data/hooks/eventHooks';
import {useLastCommonEvent} from '../../../../data/hooks/events/useLastCommonEvent';
import {useI18n} from '../../../../data/hooks/i18nHooks';
import {useMyUserId} from '../../../../data/hooks/sessionHooks';
import {useUser} from '../../../../data/hooks/users/useUser';
import {useIdListFromQueryString} from '../../../../misc/useIdListFromQueryString';
import WaitWhileBusy from '../../../WaitWhileBusy';
import HeaderEvent from '../../../elements/HeaderEvent/HeaderEvent';
import ParticipantHeader from '../../../elements/ParticipantHeader';
import ScrollableList from '../../../elements/ScrollableList';
import TopBar from '../../../elements/TopBar/TopBar';
import LoadChatRoom from '../../../loaders/LoadChatRoom';
import HasData from '../../../validate/HasData';
import Screen from '../../Screen';
import ChatMessage from '../common/ChatMessage';
import CreateChatMessage from '../common/CreateChatMessage';
import RelativeRedirect from '../../../elements/RelativeRedirect/RelativeRedirect';

export const GlobalChatRoom = props => {
    const {chatSessionId} = props,
        i18n = useI18n(),
        myUserId = useMyUserId(),
        chatSession = useChatSession(chatSessionId),
        otherParticipantId = useIdListFromQueryString('participant')[0] ?? chatSession?.otherUserId,
        otherParticipant = useUser(otherParticipantId),
        sessions = useChatSessions({
            filter: useMemo(() => chatSession => chatSession.otherUserId == otherParticipantId, [otherParticipantId]),
        }),
        lastCommonEvent = useLastCommonEvent(otherParticipantId),
        sessionEvent = useEvent(sessions[0]?.eventId),
        event = lastCommonEvent ?? sessionEvent,
        groupedMessages = useMemo(() => {
            const groupedMessages = [],
                messages = chain(sessions)
                    .map(session =>
                        session.messages.map(message => ({
                            ...message,
                            eventId: session.eventId,
                        }))
                    )
                    .flatten()
                    .uniqBy('id')
                    .sort((a, b) => {
                        let t1 = moment(a.postedOn).valueOf(),
                            t2 = moment(b.postedOn).valueOf();
                        return t2 - t1;
                    })
                    .value();

            let previousEventId, currentGroup;
            messages.forEach(message => {
                if (!currentGroup || previousEventId != message.eventId) {
                    previousEventId = message.eventId;
                    currentGroup = {eventId: message.eventId, messages: [message]};
                    groupedMessages.push(currentGroup);
                } else {
                    currentGroup.messages.push(message);
                }
            });

            return groupedMessages;
        }, [sessions]);

    if (!otherParticipant) return <RelativeRedirect to=".." />;

    const header = (
        <div>
            <TopBar returnURL=".." chat />
            <ParticipantHeader participant={otherParticipant} i18n={i18n} />
            <CreateChatMessage event={event} otherParticipant={otherParticipant} />
        </div>
    );


    return (
        <Screen header={header}>
            <ScrollableList>
                <br />
                {groupedMessages
                    .map((group, i) => [
                        <HeaderEvent key={['title', i]} eventId={group.eventId} />,
                        group.messages.map(message => <ChatMessage message={message} key={message.id} isMine={myUserId == message.userId} />),
                    ])
                    .flat()}
            </ScrollableList>
        </Screen>
    );
};

export default props => {
    const {chatSessionId} = useParams();

    return (
        <LoadChatRoom sessionId={chatSessionId}>
            <WaitWhileBusy>
                <HasData check={state => !chatSessionId || get(state, `chat.indexed[${chatSessionId}]`)}>
                    <HasData check={state => !chatSessionId || get(state, `chat.indexed[${chatSessionId}].messagesLoaded`)}>
                        <GlobalChatRoom {...props.match.params} />
                    </HasData>
                </HasData>
            </WaitWhileBusy>
        </LoadChatRoom>
    );
};
